/**
 * useProjectDataLoader Hook
 * Extracted from MultiProjectContext.tsx
 * Handles loading individual project data from server or localStorage
 */

import { useCallback, useEffect, useRef } from 'react';
import { ProjectData } from '@/shared/types/projectData';
import { API_BASE_URL, API_ENDPOINTS } from '@/shared/apiClient';
import { authService } from '@/features/auth/services/authService';
import { getAdminViewAsUserId } from '@/features/auth/services/adminViewAs';
import { transformProjectDataFromBackend } from '@/shared/utils/fieldNameTransform';
import { sanitizeProjectData } from '@/shared/utils/projectDataUtils';
import { log } from '@/shared/utils/logger';
import { yieldToMain } from '@/shared/utils/scheduler';
import { getProjectColor } from '../utils/projectUtils';
import type { ProjectDataWithMeta, ProjectInfoWithColor } from '../types/multiProject';
import type { ProjectInfo } from '@/features/projects/services/projectService';

interface UseProjectDataLoaderParams {
  projectsData: Map<string, ProjectDataWithMeta>;
  loadingProjectIds: Set<string>;
  availableProjects: ProjectInfo[];
  setProjectsData: (updater: (prev: Map<string, ProjectDataWithMeta>) => Map<string, ProjectDataWithMeta>) => void;
  setLoadingProjectIds: (updater: (prev: Set<string>) => Set<string>) => void;
  // Projects whose latest load failed (not aborted), so the UI can say so and offer a retry
  // instead of showing a checked project with no rows.
  setFailedProjectIds?: (updater: (prev: Set<string>) => Set<string>) => void;
}

const getAuthorizationScope = () => `${authService.getSessionIdentity() || 'anonymous'}|view-as:${getAdminViewAsUserId() || ''}`;

const isAbortError = (error: unknown): boolean =>
  typeof error === 'object' && error !== null && 'name' in error && (error as { name?: unknown }).name === 'AbortError';

const withoutProjectId = (prev: Set<string>, projectId: string): Set<string> => {
  if (!prev.has(projectId)) return prev;
  const next = new Set(prev);
  next.delete(projectId);
  return next;
};

export function useProjectDataLoader({
  projectsData,
  availableProjects,
  setProjectsData,
  setLoadingProjectIds,
  setFailedProjectIds,
}: UseProjectDataLoaderParams) {
  const inFlightLoadsRef = useRef<Map<string, Promise<ProjectDataWithMeta>>>(new Map());
  const inFlightLoadScopesRef = useRef<Map<string, string>>(new Map());
  const loadGenerationsRef = useRef<Map<string, number>>(new Map());
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());
  const isMountedRef = useRef(true);
  const loadedProjectScopesRef = useRef<Map<string, string>>(new Map());
  const authorizationScopeKey = getAuthorizationScope();
  const authorizationScopeRef = useRef(authorizationScopeKey);
  authorizationScopeRef.current = authorizationScopeKey;

  useEffect(() => {
    isMountedRef.current = true;
    const abortControllers = abortControllersRef.current;
    const inFlightLoads = inFlightLoadsRef.current;
    const inFlightLoadScopes = inFlightLoadScopesRef.current;
    return () => {
      isMountedRef.current = false;
      abortControllers.forEach((controller) => controller.abort());
      abortControllers.clear();
      inFlightLoads.clear();
      inFlightLoadScopes.clear();
    };
  }, []);

  useEffect(() => {
    const abortControllers = abortControllersRef.current;
    const inFlightLoads = inFlightLoadsRef.current;
    const inFlightLoadScopes = inFlightLoadScopesRef.current;
    abortControllers.forEach((controller) => controller.abort());
    abortControllers.clear();
    inFlightLoads.clear();
    inFlightLoadScopes.clear();
    loadedProjectScopesRef.current.clear();
    setLoadingProjectIds(() => new Set());
    setFailedProjectIds?.(() => new Set());
  }, [authorizationScopeKey, setLoadingProjectIds, setFailedProjectIds]);

  const loadProjectData = useCallback(
    async (projectId: string, options?: { forceReload?: boolean; shouldApply?: () => boolean }): Promise<ProjectDataWithMeta> => {
      if (!projectId) {
        log.error('MultiProjectContext', 'loadProjectData called with undefined projectId');
        throw new Error('Project ID is undefined - cannot load project data');
      }

      const forceReload = options?.forceReload === true;
      const loadAuthorizationScope = authorizationScopeKey;
      if (getAuthorizationScope() !== loadAuthorizationScope) {
        throw new DOMException('Project load belongs to an earlier session', 'AbortError');
      }
      const existing = projectsData.get(projectId);
      if (!forceReload && existing?.isLoaded && loadedProjectScopesRef.current.get(projectId) === loadAuthorizationScope) {
        return existing;
      }

      const existingLoad = inFlightLoadsRef.current.get(projectId);
      const existingLoadScope = inFlightLoadScopesRef.current.get(projectId);
      if (!forceReload && existingLoad && existingLoadScope === loadAuthorizationScope) {
        return existingLoad;
      }

      const generation = (loadGenerationsRef.current.get(projectId) ?? 0) + 1;
      loadGenerationsRef.current.set(projectId, generation);
      setFailedProjectIds?.(prev => withoutProjectId(prev, projectId));

      abortControllersRef.current.get(projectId)?.abort();
      const abortController = new AbortController();
      abortControllersRef.current.set(projectId, abortController);

      let settleLoad!: {
        resolve: (value: ProjectDataWithMeta) => void;
        reject: (reason?: unknown) => void;
      };
      const loadPromise = new Promise<ProjectDataWithMeta>((resolve, reject) => {
        settleLoad = { resolve, reject };
      });
      inFlightLoadsRef.current.set(projectId, loadPromise);
      inFlightLoadScopesRef.current.set(projectId, loadAuthorizationScope);

      void (async (): Promise<void> => {
        setLoadingProjectIds(prev => new Set(prev).add(projectId));

        try {
          let data: ProjectData;

          if (projectId === 'default') {
            const stored = localStorage.getItem('projectData');
            if (stored) {
              try {
                await yieldToMain();
                data = sanitizeProjectData(JSON.parse(stored), { logRepairs: false });
              } catch {
                data = createEmptyProjectData(projectId);
              }
            } else {
              data = createEmptyProjectData(projectId);
            }
          } else {
            const endpoint = API_ENDPOINTS.projects.getById(projectId);
            const response = await authService.authenticatedFetch(`${API_BASE_URL}${endpoint}`, {
              signal: abortController.signal,
            });

            if (!response.ok) {
              throw new Error(`Failed to load project ${projectId}`);
            }

            const responseData = await response.json();
            const rawData = responseData.data?.project || responseData.data || responseData;

            await yieldToMain();
            data = sanitizeProjectData(transformProjectDataFromBackend(rawData), { logRepairs: false });
          }

          const catalogProject = availableProjects.find(p => p.projectId === projectId);
          const projectInfo: ProjectInfoWithColor = {
            ...(catalogProject || {
              projectId,
              role: 'viewer' as const,
              lastAccessed: new Date().toISOString(),
              createdAt: data.createdAt || new Date().toISOString(),
              isFavorite: false,
              isPinned: false,
            }),
            projectId,
            // The project payload is authoritative for mutable metadata. A stale
            // catalog entry can arrive after a rename and otherwise revert Gantt headers.
            projectName: data.projectName || catalogProject?.projectName || `project-${projectId}`,
            description: data.description ?? catalogProject?.description ?? '',
            // The payload carries an explicit null once Company/Client are cleared; the
            // catalog only fills in when the payload has no key (e.g. the local default project).
            client: 'client' in data ? data.client ?? undefined : catalogProject?.client,
            companyName: 'companyName' in data ? data.companyName ?? undefined : catalogProject?.companyName,
            projectSlug: data.projectSlug ?? catalogProject?.projectSlug,
            projectAssigneeId:
              data.projectAssigneeId ??
              data.metadata?.settings?.projectAssigneeId ??
              catalogProject?.projectAssigneeId,
            leadAgentProfileId:
              data.leadAgentProfileId ??
              data.metadata?.settings?.leadAgentProfileId ??
              catalogProject?.leadAgentProfileId,
            status: data.status ?? catalogProject?.status ?? 'active',
            updatedAt: data.updatedAt || catalogProject?.updatedAt,
            nodeCount: data.nodes.length,
          };

          const projectDataWithMeta: ProjectDataWithMeta = {
            ...data,
            projectInfo,
            color: getProjectColor(projectId, projectInfo.color),
            isLoaded: true,
            lastSynced: new Date()
          };

          if (getAuthorizationScope() !== loadAuthorizationScope) {
            throw new DOMException('Project load belongs to an earlier session', 'AbortError');
          }
          if (
            !isMountedRef.current ||
            authorizationScopeRef.current !== loadAuthorizationScope ||
            loadGenerationsRef.current.get(projectId) !== generation
          ) {
            settleLoad.resolve(projectDataWithMeta);
            return;
          }

          setProjectsData(prev => {
            if (
              !isMountedRef.current ||
              abortController.signal.aborted ||
              options?.shouldApply?.() === false ||
              (authorizationScopeRef.current !== loadAuthorizationScope || getAuthorizationScope() !== loadAuthorizationScope) ||
              loadGenerationsRef.current.get(projectId) !== generation
            ) return prev;
            loadedProjectScopesRef.current.set(projectId, loadAuthorizationScope);
            const newMap = new Map(prev);
            newMap.set(projectId, projectDataWithMeta);
            return newMap;
          });

          settleLoad.resolve(projectDataWithMeta);
          return;
        } catch (error) {
          if (
            !isAbortError(error) &&
            isMountedRef.current &&
            loadGenerationsRef.current.get(projectId) === generation &&
            getAuthorizationScope() === loadAuthorizationScope
          ) {
            setFailedProjectIds?.(prev => new Set(prev).add(projectId));
          }
          settleLoad.reject(error);
        } finally {
          const isCurrentGeneration = loadGenerationsRef.current.get(projectId) === generation;
          if (isCurrentGeneration && isMountedRef.current && getAuthorizationScope() === loadAuthorizationScope) {
            setLoadingProjectIds(prev => {
              const next = new Set(prev);
              next.delete(projectId);
              return next;
            });
          }
          if (isCurrentGeneration && inFlightLoadsRef.current.get(projectId) === loadPromise) {
            inFlightLoadsRef.current.delete(projectId);
            inFlightLoadScopesRef.current.delete(projectId);
          }
          if (abortControllersRef.current.get(projectId) === abortController) {
            abortControllersRef.current.delete(projectId);
          }
        }
      })();

      return loadPromise;
    },
    [authorizationScopeKey, projectsData, availableProjects, setProjectsData, setLoadingProjectIds, setFailedProjectIds]
  );

  return { loadProjectData };
}

function createEmptyProjectData(projectId: string): ProjectData {
  return {
    version: '2.0',
    projectId,
    projectName: projectId === 'default' ? 'Default Project' : `Project ${projectId}`,
    nodes: [],
    rootNodeIds: [],
    kanbanColumns: [],
    files: [],
    fileAuditLogs: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    metadata: {
      settings: {
        darkMode: false,
        autoSync: true,
        syncInterval: 30,
        isCompactView: false,
        menuOrder: [],
        visibilityTierDefault: 'internal',
      }
    }
  };
}
